'use client';

import { BuilderComponent, builder } from '@builder.io/react';
import {
  FunctionComponent,
  useCallback,
  type AnchorHTMLAttributes
} from 'react';

import { registerComponents } from '@/react/components/content/registry/builder-registry';
import { Link } from '@/react/components/core-ui/Link';
import BuilderService from '@/services/isomorphic/integrations/BuilderService';
import type { Content } from '@/services/isomorphic/ContentService';

registerComponents();

export interface IBuilderRenderProps {
  /**
   * The content already fetched from builder.io.
   */
  content: Content;
  /**
   * Optional callback for handling when links are clicked.
   */
  handleLinkClick?: () => void;
}

/**
 * Renders content that is specifically for builder.io. This should be preprepared and verified
 * to be in the correct shape.
 */
export const BuilderContent: FunctionComponent<IBuilderRenderProps> = ({
  content,
  handleLinkClick
}) => {
  builder.init(BuilderService.builderPublicKey);
  const renderLink = useCallback(
    (props: AnchorHTMLAttributes<any>) => {
      const linkProps = {
        ...props,
        // Since there might be other onclick prop, we will only pass handleLinkClick if it is not undefined
        ...(handleLinkClick ? { onClick: handleLinkClick } : {})
      };

      return (
        // Builder.io does not pass in a width property to the link, making it not show
        // up correctly in Safari.
        <Link variant="text" style={{ width: '100%' }} {...linkProps}>
          {props.children}
        </Link>
      );
    },
    [handleLinkClick]
  );

  return (
    content.data && (
      // This keeps the footer from disturbing the CLS.
      <div id="content-wrapper">
        <BuilderComponent
          model={content.type}
          content={content.data}
          renderLink={renderLink}
        />
      </div>
    )
  );
};

export default BuilderContent;
